import { forwardRef, useState } from "react";
import { UpcomingBadge } from '@/components/site/UpcomingBadge';
import { RELEASE_OF_PATH } from '@/seo/route-meta';
import { Link } from "react-router-dom";
import { Check, Linkedin, Facebook, Instagram, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { EyeCaptainLogo } from "@/components/brand/EyeCaptainLogo";
import { subscribeNewsletter } from "@/lib/leads";
import { MONO } from "@/components/home-a/theme";
import { SOCIAL_PROFILES } from "@/config/socials";

/*
 * The menu carries the core pages only. The footer also links the pages that
 * exist for search (site types, platforms, comparisons), so every one of them
 * is one click from any page and crawlers find them without the sitemap.
 */
const COLUMNS: { title: string; links: [string, string][] }[] = [
  {
    title: "Audits",
    links: [
      ["E-commerce CRO Audit", "/solutions/ecommerce"],
      ["Lead Generation CRO Audit", "/solutions/lead-generation"],
      ["AI Predictive Attention", "/features/ai-heatmaps"],
      ["Content Audit", "/solutions/content-analysis"],
      ["Technical Audit", "/solutions/technical-analysis"],
      ["Competition Comparison", "/solutions/competitor-comparison"],
      ["Funnel Audit", "/solutions/funnel-analysis"],
      ["User Agent", "/solutions/user-agent"],
    ],
  },
  {
    title: "By platform",
    links: [
      ["Shopify CRO Audit", "/shopify-cro-audit"],
      ["WooCommerce CRO Audit", "/woocommerce-cro-audit"],
      ["BigCommerce CRO Audit", "/bigcommerce-cro-audit"],
      ["Magento CRO Audit", "/magento-cro-audit"],
      ["Wix CRO Audit", "/wix-cro-audit"],
      ["Squarespace CRO Audit", "/squarespace-cro-audit"],
      ["Webflow CRO Audit", "/webflow-cro-audit"],
    ],
  },
  {
    title: "By site type",
    links: [
      ["CRO Page Audit", "/cro-page-audit"],
      ["SaaS CRO", "/solutions/saas"],
      ["Booking site CRO", "/solutions/booking"],
      ["Content site CRO", "/solutions/information-sites"],
      ["User Agent", "/solutions/user-agent"],
      ["Free CRO audit", "/free-audit"],
    ],
  },
  {
    title: "Company",
    links: [
      ["What is EyeCaptain", "/what-is-eyecaptain"],
      ["How it works", "/how-it-works"],
      ["For agencies", "/agencies"],
      ["Pricing", "/pricing"],
      ["FAQ", "/faq"],
      ["Contact", "/contact"],
    ],
  },
  {
    title: "Learn",
    links: [
      ["What is CRO", "/what-is-cro"],
      ["CRO audit guide", "/cro-audit"],
      ["CRO checklist", "/cro-checklist"],
      ["E-commerce CRO guide", "/ecommerce-cro-guide"],
      ["WooCommerce checkout guide", "/woocommerce-checkout-optimization"],
      ["Shopify product page guide", "/shopify-product-page-best-practices"],
      ["Webflow landing page checklist", "/webflow-landing-page-checklist"],
      ["Heatmap analysis guide", "/heatmap-analysis-guide"],
      ["A/B testing guide", "/ab-testing-guide"],
      ["Neuromarketing", "/features/neuromarketing"],
      ["CRO techniques", "/features/cro-techniques"],
      ["Blog", "/blog"],
    ],
  },
  {
    title: "Upcoming",
    links: [
      ["Live heatmaps", "/features/live-heatmaps"],
      ["A/B testing", "/features/ab-testing"],
      ["Analytics integrations", "/features/analytics-integrations"],
      ["Visual mockups", "/features/visual-mockups"],
      ["AI Agent", "/features/ai-agent"],
      ["Figma design audit", "/features/figma-design-audit"],
    ],
  },
  {
    title: "Compare",
    links: [
      ["vs Hotjar", "/compare/eyecaptain-vs-hotjar"],
      ["vs Microsoft Clarity", "/compare/eyecaptain-vs-microsoft-clarity"],
      ["vs VWO", "/compare/eyecaptain-vs-vwo"],
      ["vs Attention Insight", "/compare/eyecaptain-vs-attention-insight"],
    ],
  },
];

const TikTok = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.1a8.16 8.16 0 0 0 4.76 1.52v-3.4c-.83 0-1.63-.17-2.36-.48z" />
  </svg>
);

const SOCIALS = [
  { href: SOCIAL_PROFILES.linkedin, label: "LinkedIn", Icon: Linkedin },
  { href: SOCIAL_PROFILES.facebook, label: "Facebook", Icon: Facebook },
  { href: SOCIAL_PROFILES.instagram, label: "Instagram", Icon: Instagram },
  { href: SOCIAL_PROFILES.tiktok, label: "TikTok", Icon: TikTok },
];

/** The newsletter, as one line in the footer rather than a section of its own. */
function FooterNewsletter() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "done">("idle");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || state === "loading") return;
    setState("loading");
    try {
      const { data, error } = await subscribeNewsletter({ email, source: "footer_section" });
      if (error) throw error;
      if (data?.already_subscribed) toast.info(data.message || "You are already on the list.");
      setState("done");
      setEmail("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not subscribe. Please try again.");
      setState("idle");
    }
  };

  if (state === "done") {
    return (
      <p className="flex items-center gap-2 text-[13.5px] text-[hsl(var(--ec-text-2))]">
        <span className="grid h-5 w-5 place-items-center rounded-full bg-primary/15 text-[hsl(var(--ec-mint))]"><Check className="h-3 w-3" /></span>
        Thanks, you are on the list.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="w-full max-w-[380px]">
      <label htmlFor="footer-email" className="text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>
        Weekly CRO notes
      </label>
      <div className="mt-2 flex items-center gap-1 rounded-[12px] border border-[hsl(var(--ec-line-strong))] bg-foreground/[0.02] p-1 focus-within:border-[hsl(var(--ec-mint)/0.5)]">
        <input
          id="footer-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          className="h-9 min-w-0 flex-1 bg-transparent px-3 text-[14px] text-foreground outline-none placeholder:text-[hsl(var(--ec-muted))]"
        />
        <button
          type="submit"
          aria-label="Subscribe"
          disabled={state === "loading"}
          className="ec-cta-white inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[9px] px-3.5 text-[13px] font-semibold disabled:opacity-70"
        >
          {state === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>Subscribe</>}
        </button>
      </div>
      <p className="mt-2 text-[12px] text-[hsl(var(--ec-muted))]">One short email a week. Unsubscribe any time.</p>
    </form>
  );
}

/** Links to features released in stages (Admin › Releases) carry an "Upcoming" badge until live for everyone. */
const RELEASE_OF: Readonly<Record<string, string>> = RELEASE_OF_PATH;

const Footer = forwardRef<HTMLDivElement>((_, ref) => {
  const year = new Date().getFullYear();
  return (
    <div ref={ref}>
      <footer className="relative overflow-hidden border-t border-border bg-background text-foreground">
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_50%_60%_at_0%_0%,hsl(var(--primary)/0.07),transparent_70%)]" />
        <div className="relative mx-auto max-w-[1200px] px-4 pb-8 pt-14 sm:px-6 md:pt-20">
          <div className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-[minmax(0,1.6fr)_repeat(7,minmax(0,1fr))] lg:gap-6">
            <div className="col-span-2 lg:col-span-1">
              <Link to="/" className="inline-flex" aria-label="EyeCaptain home">
                <EyeCaptainLogo alt="EyeCaptain" width={160} height={40} className="h-9 w-auto" loading="lazy" decoding="async" />
              </Link>
              <p className="mt-4 max-w-[340px] text-[14px] leading-relaxed text-[hsl(var(--ec-text-2))]">
                AI CRO audit that shows what stops visitors on your page, and what to change first.
              </p>
              <div className="mt-7">
                <FooterNewsletter />
              </div>
              <div className="mt-7 flex items-center gap-2">
                {SOCIALS.map(({ href, label, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`EyeCaptain on ${label}`}
                    className="grid h-10 w-10 place-items-center rounded-[10px] border border-border text-[hsl(var(--ec-text-2))] transition-colors hover:border-[hsl(var(--ec-mint)/0.45)] hover:text-[hsl(var(--ec-mint))]"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>

            {COLUMNS.map((col) => (
              <nav key={col.title} aria-label={col.title}>
                <p className="text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--ec-muted))]" style={{ fontFamily: MONO }}>
                  {col.title}
                </p>
                <ul className="mt-4 space-y-0.5 md:space-y-2.5">
                  {col.links.map(([label, href]) => (
                    <li key={href}>
                      <Link to={href} className="inline-flex flex-wrap items-center gap-1.5 py-1.5 text-[14px] text-[hsl(var(--ec-text-2))] transition-colors hover:text-[hsl(var(--ec-mint))] md:py-0">
                        {label}
                        {RELEASE_OF[href] && col.title !== "Upcoming" && <UpcomingBadge release={RELEASE_OF[href]} short />}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>

          <div className="mt-14 flex flex-col gap-4 border-t border-border pt-6 text-[13px] text-[hsl(var(--ec-muted))] md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
              {/* Prerendered at build time: after New Year, until the next build, the
                  browser's year differs from the HTML's. Not worth re-rendering the page. */}
              <span suppressHydrationWarning>© {year} EyeCaptain</span>
              <a href="mailto:hello@eyecaptain.io" className="transition-colors hover:text-[hsl(var(--ec-mint))]">hello@eyecaptain.io</a>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 pb-[env(safe-area-inset-bottom)]">
              {[
                ["/terms", "Terms"],
                ["/privacy", "Privacy"],
                ["/cookies", "Cookies"],
              ].map(([to, label]) => (
                <Link key={to} to={to} className="py-1.5 transition-colors hover:text-[hsl(var(--ec-mint))] md:py-0">
                  {label}
                </Link>
              ))}
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event("ec:cookie-settings"))}
                className="py-1.5 transition-colors hover:text-[hsl(var(--ec-mint))] md:py-0"
              >
                Cookie settings
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
});
Footer.displayName = "Footer";
export default Footer;
